/**
 * Create Solution — vertical composition with shared media + URL metadata.
 */
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { useUser } from "util/UserContext";
import { useAuthGate } from "hooks/useAuthGate";
import { usePostFormLifecycle } from "hooks/usePostFormLifecycle";
import { useMediaManager } from "hooks/useMediaManager";
import { useResolvedIssue, type IssueEntityInput } from "hooks/useResolvedIssue";
import { useCoauthorManager } from "hooks/useCoauthorManager";
import { usePrivacyDefaults } from "hooks/usePrivacyDefaults";
import { useUrlTitlePrefill } from "hooks/useUrlTitlePrefill";
import TextEditor from "components/TextEditor";
import { createSolutionForIssue, addCoauthors } from "util/posts";
import { Solution } from "util/constants";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { solutionCreateSchema } from "lib/validation/contentSchemas";
import { standardizeUrl } from "util/textUtils";
import PostFormShell from "components/Posts/PostFormShell";
import MediaSection from "components/Posts/sections/MediaSection";
import LinkField from "components/Posts/sections/LinkField";
import TitleField from "components/Posts/sections/TitleField";
import CoauthorSection from "components/Posts/sections/CoauthorSection";
import PrivacySection from "components/Posts/sections/PrivacySection";
import FormStatusMessage from "components/Posts/sections/FormStatusMessage";
import ContextCommentDraftList, {
  type ContextCommentDraft,
} from "components/Posts/sections/ContextCommentDraftList";

export interface SolutionFormHandle {
  submit: () => void;
}

interface Props {
  entity: IssueEntityInput;
  onSuccess: (solution: Solution) => void;
  onCancel: () => void;
  hideActions?: boolean;
  onCanSubmitChange?: (canSubmit: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

const SolutionPostForm = forwardRef<SolutionFormHandle, Props>(
  (
    {
      entity,
      onSuccess,
      onCancel,
      hideActions = false,
      onCanSubmitChange,
      onSubmittingChange,
    },
    ref
  ) => {
    const { currentUser } = useAuthGate();
    const { setShowAuthModal } = useUser();
    const { resolvedIssue: solutionIssue } = useResolvedIssue(entity);
    const { privacy, setPrivacy } = usePrivacyDefaults();
    const { coauthors, addCoauthor, removeCoauthor, coauthorPairs } = useCoauthorManager(privacy);

    const mm = useMediaManager(20);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [referenceLink, setReferenceLink] = useState("");
    const [showLinkField, setShowLinkField] = useState(false);
    const [showTitleField, setShowTitleField] = useState(false);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [principleDrafts, setPrincipleDrafts] = useState<ContextCommentDraft[]>([]);

    const { loading } = useUrlTitlePrefill({
      linkEnabled: showLinkField,
      referenceLink,
      titleToggleRequiredForPrefill: false,
      titleEnabled: showTitleField,
      setTitle: setName,
    });

    const lifecycle = usePostFormLifecycle({
      getValues: useCallback(
        () => ({
          showLinkField,
          showTitleField,
          referenceLink,
          name,
          description,
          mediaCount: mm.media.length,
          hasIssue: Boolean(solutionIssue?.id),
        }),
        [showLinkField, showTitleField, referenceLink, name, description, mm.media.length, solutionIssue]
      ),
      schema: solutionCreateSchema,
      isReady: true,
      onCanSubmitChange,
      onSubmittingChange,
    });

    const uploadMediaFiles = useCallback(
      async (solutionId: number) => {
        if (mm.media.length === 0) return;
        setIsUploadingMedia(true);
        try {
          await uploadEntityMediaDirect(
            "solution",
            solutionId,
            mm.media.map((mediaFile) => ({
              file: mediaFile.file,
              order: mediaFile.order,
              intrinsic: mediaFile.intrinsic,
            }))
          );
        } finally {
          setIsUploadingMedia(false);
        }
      },
      [mm.media]
    );

    const submit = useCallback(async () => {
      if (!currentUser) {
        setShowAuthModal(true);
        return;
      }
      const validationError = lifecycle.validateOrError();
      if (validationError) {
        lifecycle.setError(validationError);
        return;
      }
      if (!lifecycle.canSubmit || !solutionIssue) return;

      lifecycle.beginSubmit();

      try {
        const linkRaw = referenceLink.trim();
        const link = showLinkField ? standardizeUrl(linkRaw) : null;

        const finalTitle = showTitleField ? name.trim() || "[no_title]" : "[no_title]";
        const finalLink = showLinkField ? link : null;

        // Inline principle drafts get attached atomically by the
        // backend during `POST /api/posts`. We send only meaningful
        // drafts (non-empty new content or picked existing ids) so the
        // wire payload stays minimal. The backend handles anonymous
        // hosts correctly (stores the new comments with user_id=0 and
        // privacy='anonymous').
        const principlePrivacy = privacy;
        const inlinePrincipleDrafts: Record<string, unknown>[] = principleDrafts
          .map((draft): Record<string, unknown> | null => {
            if (draft.source === "existing") {
              return {
                local_id: draft.localId,
                existing_comment_id: draft.existingCommentId,
              };
            }
            const trimmed = draft.content.trim();
            if (!trimmed || trimmed === "<p></p>") {
              return null;
            }
            return {
              local_id: draft.localId,
              content: trimmed,
              privacy: principlePrivacy,
            };
          })
          .filter((draft): draft is Record<string, unknown> => draft !== null);

        const payload = {
          title: showTitleField ? finalTitle : "[no_title]",
          body: description.trim(),
          url: finalLink,
          user_id: currentUser.id,
          privacy,
          properties: { privacy },
          principles: inlinePrincipleDrafts.length > 0 ? inlinePrincipleDrafts : undefined,
        };

        const created = Object.assign({} as Solution, await createSolutionForIssue(solutionIssue.id, payload));

        if (coauthors.length > 0 && privacy !== "anonymous") {
          try {
            await addCoauthors("solution", created.id, coauthorPairs);
          } catch {
            /* best-effort */
          }
        }

        if (mm.media.length > 0) {
          try {
            await uploadMediaFiles(created.id);
          } catch {
            /* best-effort */
          }
        }

        toast.success("Solution added successfully");
        onSuccess(created);
      } catch {
        toast.error("Failed to add solution");
      } finally {
        lifecycle.endSubmit();
      }
    }, [
      currentUser,
      lifecycle,
      solutionIssue,
      referenceLink,
      name,
      description,
      privacy,
      setShowAuthModal,
      coauthorPairs,
      mm.media.length,
      onSuccess,
      uploadMediaFiles,
      showLinkField,
      showTitleField,
      coauthors.length,
      principleDrafts,
    ]);

    useImperativeHandle(ref, () => ({ submit: () => void submit() }), [submit]);

    const handleLinkToggle = useCallback(() => {
      if (showLinkField) {
        setShowLinkField(false);
        setReferenceLink("");
      } else {
        setShowLinkField(true);
        setShowTitleField(true);
      }
    }, [showLinkField]);

    const linkField = useMemo(
      () => (
        <LinkField
          id="solution-reference-link"
          name="solution-reference-link"
          labelText="Reference URL"
          value={referenceLink}
          onChange={setReferenceLink}
        />
      ),
      [referenceLink]
    );

    const titleField = useMemo(
      () => (
        <TitleField
          id="solution-name"
          name="solution-name"
          labelText="Title"
          fieldRequired={false}
          value={name}
          onChange={setName}
          maxLength={300}
          loading={loading}
          validationState={name.trim().length > 0 ? "valid" : "none"}
        />
      ),
      [name, loading]
    );

    return (
      <PostFormShell intro="Share a solution to this issue; solutions are means to maximize positive and minimize negative impacts of an issue.">
        <MediaSection
          idPrefix="solution"
          mm={mm}
          showLink={showLinkField}
          showTitle={showTitleField}
          onToggleLink={handleLinkToggle}
          onToggleTitle={() => setShowTitleField((prev) => !prev)}
          linkField={linkField}
          titleField={titleField}
        />

        <div className="mt-0">
          <TextEditor
            value={description}
            onChange={setDescription}
            editorType="entity"
            placeholder="Body"
            fieldRequired={false}
          />
        </div>

        <ContextCommentDraftList
          kind="principle"
          drafts={principleDrafts}
          onDraftsChange={setPrincipleDrafts}
          parentPrivacy={privacy}
        />

        {privacy !== "anonymous" ? (
          <CoauthorSection coauthors={coauthors} onAdd={addCoauthor} onRemove={removeCoauthor} />
        ) : null}

        <PrivacySection privacy={privacy} onPrivacyChange={setPrivacy} miniIcon={false} showDropdown />

        <FormStatusMessage error={lifecycle.error} />

        {!hideActions ? (
          <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
            <button type="button" onClick={onCancel} className="btn-secondary w-full sm:w-fit py-2 px-4">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!lifecycle.canSubmit || isUploadingMedia}
              onClick={(e) => {
                e.preventDefault();
                void submit();
              }}
              className={`w-full sm:w-fit py-2 px-4 ${
                !lifecycle.canSubmit || isUploadingMedia
                  ? "btn-primary-disabled"
                  : "btn-primary"
              } ${lifecycle.isSubmitting || isUploadingMedia ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {lifecycle.isSubmitting || isUploadingMedia ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                </span>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        ) : null}
      </PostFormShell>
    );
  }
);

SolutionPostForm.displayName = "SolutionPostForm";

export default SolutionPostForm;
